import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Loader2 } from 'lucide-react';

export function ProjectTaskDialog({
  open, onOpenChange, taskForm, setTaskForm, onSubmit, taskLoading, staffMembers,
}) {
  const toggleAssignee = (userId) => {
    if (taskForm.assignees.includes(userId)) {
      setTaskForm({ ...taskForm, assignees: taskForm.assignees.filter(id => id !== userId) });
    } else {
      setTaskForm({ ...taskForm, assignees: [...taskForm.assignees, userId] });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add New Task</DialogTitle>
          <DialogDescription>Create a task for this project</DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="form-label">Task Title</Label>
              <Input
                value={taskForm.title}
                onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                placeholder="e.g., Review floor plans"
                required
                data-testid="task-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="form-label">Description</Label>
              <Textarea
                value={taskForm.description}
                onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                placeholder="Task details..."
                rows={2}
                data-testid="task-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="form-label">Due Date</Label>
                <Input
                  type="date"
                  value={taskForm.due_date}
                  onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })}
                  data-testid="task-due-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="form-label">Priority</Label>
                <Select
                  value={taskForm.priority}
                  onValueChange={(value) => setTaskForm({ ...taskForm, priority: value })}
                >
                  <SelectTrigger data-testid="task-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="form-label">Assign To (Select Multiple)</Label>
              <div className="border border-border rounded-md max-h-48 overflow-y-auto">
                {staffMembers.length === 0 ? (
                  <p className="p-4 text-sm text-muted-foreground text-center">
                    No team members available
                  </p>
                ) : (
                  staffMembers.map(member => (
                    <div
                      key={member.user_id}
                      className="flex items-center gap-3 p-3 border-b border-border last:border-b-0 hover:bg-muted/50"
                    >
                      <Checkbox
                        id={`task-assignee-${member.user_id}`}
                        checked={taskForm.assignees.includes(member.user_id)}
                        onCheckedChange={() => toggleAssignee(member.user_id)}
                      />
                      <Label
                        htmlFor={`task-assignee-${member.user_id}`}
                        className="flex-1 cursor-pointer text-sm font-normal"
                      >
                        {member.name}
                        <span className="text-muted-foreground ml-2">({member.role})</span>
                      </Label>
                    </div>
                  ))
                )}
              </div>
              {taskForm.assignees.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  {taskForm.assignees.length} team member(s) selected
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              className="btn-primary"
              disabled={taskLoading}
              data-testid="create-task-submit-btn"
            >
              {taskLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Task'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
